import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { VideoPlayer } from '@components';
import LinkShareIcon from './LinkShareIcon';
import UnmutedIcon from './unmute.svg?react';
import MuteIcon from './mute.svg?react';
import styles from '../LessonProgressModal.module.scss';

type Props = {
  metaData?: {};
  title?: string;
  src?: string | null;
  playing: boolean
  onPlayPreviewVideo?: () => void;
  uniqueName?: string | null;
  handleClickShare?: () => void;
};

export type VideoPlayerRef = {
  setMute: (muted: boolean) => void;
};

const CoverVideoPlayer = forwardRef<VideoPlayerRef, Props>((props, ref) => {
  const {title, src, metaData, playing, onPlayPreviewVideo, uniqueName, handleClickShare } = props;

  const [muted, setMuted] = useState(true);
  const [replay, setReplay] = useState(false);
  const [firstPlay, setFirstPlay] = useState(false);
  const [muteCount, setMuteCount] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const muteRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    setMute: (shouldMute: boolean) => {
      setMuted(shouldMute);
    }
  }));

  useEffect(() => {
    setReplay(playing)
  }, [playing]);

  useEffect(() => {
    let timeout: any;
  
    const displayOpa = () => {
      if (muteRef.current) {
        muteRef.current.style.opacity = "1"
      }
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        if (muteRef.current) {
          muteRef.current.style.opacity = "0"
        }
      }, 2000);
    };

    let refWrapper: any = null;
    if (wrapperRef.current) {
      refWrapper = wrapperRef.current;
      refWrapper.addEventListener('mousemove', displayOpa);
    }

    return () => {
      if (refWrapper) {
        refWrapper.removeEventListener('mousemove', displayOpa);
      }
    };
  }, []);

  const handleVideo = () => {
    setMuted(current => !current)
    setMuteCount(current => current + 1);
    if (muteCount === 0) {
      setFirstPlay(true);
      onPlayPreviewVideo?.();
    } else {
      setFirstPlay(false);
    }
  }

  const handleEnd = useCallback(() => {
    setMuted(true);
  }, []);

  return (
    <div ref={wrapperRef} className={styles.videoView} onClick={handleVideo}>
      <VideoPlayer 
        isCoverVideo 
        src={src} 
        metaData={metaData || {video_title: 'Cover Video'}} 
        autoPlay={false} 
        muted={muted} 
        className={styles.video} 
        loop={false} 
        controls={false} 
        replay={replay} 
        onNext={handleEnd} 
        firstPlay={firstPlay}
      />
      {muted && (
        <>
          <div className={styles.gradientView}></div>
          <div className={styles.coverTitle}>
            <div className={styles.textView}>
              <h2>{title}</h2>
            </div>
          </div>
        </>
      )}
      {muted ? (
        <div className={styles.unmuteIcon} style={{opacity: muted ? '1': 'none'}}>
          <UnmutedIcon />
        </div>
      ) : (
        <div ref={muteRef} className={styles.muteIcon}>
          <MuteIcon />
        </div>
      )}
      {uniqueName && (
        <div className={styles.shareIcon}>
          <LinkShareIcon uniqueName={uniqueName} handleClickShare={handleClickShare}/>
        </div>
      )}
    </div>
  );
});

export default CoverVideoPlayer;
